import { useMemo, useState } from "react";
import moment from "moment";
import { ExpandableTable } from "../../../components/UniversalTable";
import { defaultTableColumns } from "../../../helpers";
import { BankItem } from "../../../types/verified/bank";
import { toMoney } from "shared/utils";
import { Show } from "shared/components";
import { TruncateWithTooltip } from "shared/components/TruncateWithTooltip";
import { getIncomeStreamLabel } from "shared/components/IncomeStreamCard/utils";
import { KeyValue } from "views/applications-id-report/components/KeyValue";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { NameMismatchAlert } from "./NameMismatchAlert";
import { BankSourceDeposits } from "./BankSourceDeposits";
import { Tabs } from "shared/components/BaseTabs";
import { IncomeSource } from "shared/types";

type BankStreamItem = {
  id: number;
  income_id: number | null;
  data: BankItem["data"];
};

type BankSourceRow = {
  source: string;
  bankName: string | null;
  dateRange: string;
  grossMonthly: string;
  netMonthly: string;
  original: BankStreamItem;
};

const formatDateRange = (start?: string, end?: string | null) =>
  (start ? moment(start).format("ll") : "-") +
  " - " +
  (end ? moment(end).format("ll") : "Present");

const buildRow = (item: BankStreamItem): BankSourceRow => ({
  source: getIncomeStreamLabel(item.data),
  bankName: item.data.institution_metadata?.name?.trim() || null,
  dateRange: formatDateRange(item.data.start_date, item.data.end_date),
  grossMonthly:
    item.data.gross_monthly_income_amount != null
      ? (toMoney(item.data.gross_monthly_income_amount) ?? "-")
      : "-",
  netMonthly: toMoney(item.data.net_monthly_income_amount) ?? "-",
  original: item
});

const normalizeBankItem = (item: BankItem): BankStreamItem => ({
  id: item.id,
  income_id: item.income_id,
  data: item.data
});

const normalizeUnassignedItem = (
  item: IncomeSource["items"][number]
): BankStreamItem => ({
  id: item.id,
  income_id: item.income_id,
  data: item.data as unknown as BankItem["data"]
});

const TOOLTIP_CLASS_NAME = "z-[50] bg-alpha-800 break-words whitespace-normal";

const BankSourceCell = ({
  label,
  bankName
}: {
  label: string;
  bankName: string | null;
}) => (
  <div className="space-y-0.5">
    <TruncateWithTooltip
      content={label}
      className="text-sm text-content-1000"
      tooltipClassName={TOOLTIP_CLASS_NAME}
      placement="top-start"
    />
    <Show if={Boolean(bankName)}>
      <TruncateWithTooltip
        content={bankName}
        className="text-xs text-content-300"
        tooltipClassName={TOOLTIP_CLASS_NAME}
        placement="bottom-start"
      />
    </Show>
  </div>
);

const bankTableColumns = [
  {
    ...defaultTableColumns[0],
    accessor: (row: BankSourceRow) => (
      <BankSourceCell label={row.source} bankName={row.bankName} />
    ),
    widthClass: "w-full min-w-0 pr-4"
  },
  { ...defaultTableColumns[1], widthClass: "w-[210px] shrink-0" },
  { ...defaultTableColumns[2], widthClass: "w-[120px] shrink-0 text-right" },
  { ...defaultTableColumns[3], widthClass: "w-[160px] shrink-0 text-right" }
];

const BankStreamExpanded = ({ item }: { item: BankStreamItem }) => {
  const nameMismatch = item.data.name_mismatch;
  const depositDescription = item.data.income_description?.trim();
  return (
    <div className="px-6 pt-6 pb-4 bg-background-900">
      {nameMismatch ? (
        <NameMismatchAlert
          nameMismatch={nameMismatch}
          variant="linked_bank"
          className="mb-4"
        />
      ) : null}
      <ContentTitle
        title="Income details"
        marginClassName="mt-0"
        contentClassName="space-y-2"
      >
        <KeyValue
          gapClassName="gap-6"
          label="Employer name"
          value={item.data.employer?.name}
        />
        <KeyValue
          gapClassName="gap-6"
          label="Deposit description"
          value={
            depositDescription ? (
              <span className="[overflow-wrap:anywhere]">
                {depositDescription}
              </span>
            ) : (
              "-"
            )
          }
        />
        <KeyValue
          gapClassName="gap-6"
          label="Income start date"
          value={
            item.data.start_date
              ? moment(item.data.start_date).format("ll")
              : "-"
          }
        />
        <KeyValue
          gapClassName="gap-6"
          label="Most recent pay date"
          value={
            item.data.end_date ? moment(item.data.end_date).format("ll") : "-"
          }
        />
        <KeyValue
          gapClassName="gap-6"
          label="Pay frequency"
          value={item.data.pay_frequency || "-"}
        />
        <KeyValue
          gapClassName="gap-6"
          label="Transaction count"
          value={item.data.transaction_count?.toString()}
        />
        <KeyValue
          gapClassName="gap-6"
          label="Monthly net income"
          value={toMoney(item.data.net_monthly_income_amount)}
        />
        <KeyValue
          gapClassName="gap-6"
          label="Monthly gross income"
          value={toMoney(item.data.gross_monthly_income_amount)}
        />
        <KeyValue
          gapClassName="gap-6"
          label="Forecasted monthly income"
          value={toMoney(item.data.forecasted_monthly_income_amount)}
        />
      </ContentTitle>

      <Show if={item.data.deposits?.length}>
        <BankSourceDeposits deposits={item.data.deposits ?? []} />
      </Show>
    </div>
  );
};

type BankSourcesTab = "Selected" | "Other";

const tabItems = [
  { value: "Selected" as BankSourcesTab, label: "Selected income sources" },
  { value: "Other" as BankSourcesTab, label: "Other income sources" }
];

export const BankSourcesTable = ({
  income,
  plaidBankSource
}: {
  income: BankItem[];
  plaidBankSource?: IncomeSource | null;
}) => {
  const currentIncomeId = income[0]?.income_id;

  const assignedRows = useMemo(
    () => income.map((item) => buildRow(normalizeBankItem(item))),
    [income]
  );

  const unassignedRows = useMemo(() => {
    if (!plaidBankSource || currentIncomeId == null) return [];
    return plaidBankSource.items
      .filter((item) => item.income_id !== currentIncomeId)
      .map((item) => buildRow(normalizeUnassignedItem(item)));
  }, [plaidBankSource, currentIncomeId]);

  const hasOtherSources = unassignedRows.length > 0;

  const [activeTab, setActiveTab] = useState<BankSourcesTab>("Selected");

  const rows = activeTab === "Other" ? unassignedRows : assignedRows;

  return (
    <>
      {hasOtherSources ? (
        <div className="mb-4 mt-6 border-b border-alpha-80">
          <Tabs
            tabs={tabItems}
            active={activeTab}
            onChange={(t) => setActiveTab(t)}
            tabStyle="line"
            size="sm"
            className="w-fit"
          />
        </div>
      ) : null}

      <ExpandableTable
        key={activeTab}
        cellClassName="p-4 pl-0 text-sm self-center"
        headerRowClassName="text-content-300 !font-medium"
        renderExpanded={({ original }) => (
          <BankStreamExpanded item={original} />
        )}
        rows={rows}
        columns={bankTableColumns}
        hideExpandAll
      />
    </>
  );
};
